/**
 * メンバー管理。
 *
 * ここでいうメンバーは「コントロールパネルにログインできる担当者」であって、
 * メールアドレス（メールボックス）とは別物。契約者はこの 2 つを混同しやすいので、
 * 画面上で必ず区別を明記する。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { Send, Trash2, UserPlus } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { AppLink } from '../lib/nav.js';
import { ConfirmDialogButton } from '../components/confirm-button.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { HelpNote } from '../components/help.js';
import { useAuth, canManageMembers } from '../stores/auth.js';
import type { CountedList, Member } from '../lib/api-types.js';

const ROLE_LABEL: Record<Member['role'], string> = {
  org_owner: '契約者（オーナー）',
  org_admin: '管理者',
  org_member: '一般メンバー',
};

const ROLE_HELP: Record<Member['role'], string> = {
  org_owner: 'すべての操作ができます。お支払い情報とメンバーの管理も行えます。',
  org_admin: 'ドメイン・メールアドレスの管理ができます。お支払い情報は閲覧のみです。',
  org_member: '自分のメールアドレスのパスワード変更・自動応答の設定のみ行えます。',
};

export function MembersPage() {
  const { user } = useAuth();
  const canManage = canManageMembers(user);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: qk.members(),
    queryFn: () => api.get<CountedList<Member>>('/members'),
    staleTime: 60_000,
  });

  const changeRole = useMutation({
    // API のリクエストキーは `roleCode`（`role` では受け取られない）。
    mutationFn: ({ id, role }: { id: string; role: Member['role'] }) =>
      api.patch<{ ok: boolean }>(`/members/${encodeURIComponent(id)}`, { roleCode: role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.members() }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean }>(`/members/${encodeURIComponent(id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.members() }),
  });

  /*
   * **招待の再送。** 招待リンクの有効期間は 7 日で、期限切れを自力で
   * 回復する手段が無いと管理者は「メンバーを消してもう一度追加する」
   * しかなくなる（ロールも招待日も消える）。
   * 応答の `emailSent` が false なら、メール送信そのものが失敗している。
   */
  const resend = useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; emailSent: boolean }>(
        `/members/${encodeURIComponent(id)}/resend-invite`,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.members() }),
  });

  const columns: Array<DataTableColumn<Member>> = [
    {
      id: 'user',
      header: '担当者',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{row.displayName || row.email}</p>
          <p className="truncate text-xs text-muted-foreground">{row.email}</p>
        </div>
      ),
    },
    {
      id: 'role',
      header: '権限',
      width: 'w-56',
      cell: (row) =>
        canManage && row.role !== 'org_owner' ? (
          <Select
            value={row.role}
            onValueChange={(v) => changeRole.mutate({ id: row.id, role: v as Member['role'] })}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="org_admin">{ROLE_LABEL.org_admin}</SelectItem>
              <SelectItem value="org_member">{ROLE_LABEL.org_member}</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Badge variant="secondary" size="sm">
            {ROLE_LABEL[row.role]}
          </Badge>
        ),
    },
    {
      id: 'status',
      header: '状態',
      width: 'w-40',
      /*
       * **招待中は「いつ招待したか」まで出す。** 招待された本人は
       * パスワードを設定するまでログインできないので、状態だけを
       * 「招待中」と出しても、管理者には*止まっているのか進んでいるのか*が
       * 分からない。招待日を添えると「1 週間前の招待が未受諾＝メールが
       * 届いていない可能性」を管理者が自分で判断できる。
       */
      cell: (row) => (
        <div className="space-y-1">
          <StatusBadge
            status={row.status === 'active' ? 'active' : row.status === 'invited' ? 'pending' : 'disabled'}
            label={row.status === 'active' ? '利用中' : row.status === 'invited' ? '招待中' : '停止中'}
            size="sm"
            dot
          />
          {row.status === 'invited' && row.invitedAt ? (
            <p className="text-[11px] text-muted-foreground">
              {formatDateTime(row.invitedAt)} に招待
            </p>
          ) : null}
        </div>
      ),
    },
    {
      id: 'lastLogin',
      header: '最終ログイン',
      width: 'w-44',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="tabular text-xs">
          {row.lastLoginAt ? formatDateTime(row.lastLoginAt) : 'まだありません'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 'w-44',
      align: 'right',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          {canManage && row.status === 'invited' ? (
            <Button
              variant="ghost"
              size="sm"
              loading={resend.isPending && resend.variables === row.id}
              onClick={() => resend.mutate(row.id)}
            >
              <Send aria-hidden />
              招待を再送
            </Button>
          ) : null}
          {canManage && row.role !== 'org_owner' ? (
          <ConfirmDialogButton
            title={`${row.email} を削除します`}
            description="このユーザーはコントロールパネルにログインできなくなります。メールアドレス（メールボックス）は削除されません。"
            confirmLabel="削除する"
            pending={remove.isPending}
            onConfirm={() => remove.mutate(row.id)}
          >
            <Trash2 aria-hidden />
            削除
          </ConfirmDialogButton>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="メンバー"
        description="このコントロールパネルにログインできる担当者を管理します。"
        actions={
          canManage ? (
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus aria-hidden />
              メンバーを招待
            </Button>
          ) : null
        }
      />

      <div className="space-y-4">
        <HelpNote title="メールアドレスとの違い">
          ここで追加するのは「管理画面を使う担当者」です。メールの送受信に使うアドレスは
          <AppLink to="/mailboxes" className="mx-1 text-primary underline-offset-4 hover:underline">
            メールアドレス
          </AppLink>
          の画面から作成してください。
        </HelpNote>

        {changeRole.isError ? <FormAlert error={changeRole.error} /> : null}
        {resend.isError ? <FormAlert error={resend.error} /> : null}
        {resend.isSuccess ? (
          <Alert variant={resend.data.emailSent ? 'success' : 'warning'}>
            <AlertTitle>
              {resend.data.emailSent ? '招待メールを再送しました' : '招待メールを送信できませんでした'}
            </AlertTitle>
            <AlertDescription>
              {resend.data.emailSent
                ? '有効期限は再送から 7 日間です。'
                : 'メールの送信に失敗しました。時間をおいてもう一度お試しいただくか、サポートへお問い合わせください。'}
            </AlertDescription>
          </Alert>
        ) : null}

        {/*
         * **招待中のメンバーが 1 人でもいる間は、受諾の手順を必ず出す。**
         * 招待された本人は、招待メールのリンク（`/invite?token=...`）から
         * パスワードを設定するまでログインできない。この案内が無いと、
         * 管理者から見ると「招待したのに使えない」だけの状態に見える。
         */}
        {(list.data?.items ?? []).some((m) => m.status === 'invited') ? (
          <Alert variant="info">
            <AlertTitle>招待中のメンバーがいます</AlertTitle>
            <AlertDescription>
              招待された方は、招待メール内のリンクからパスワードを設定するとログインできます
              （リンクの有効期限は 7 日間）。メールが届かない場合は迷惑メールフォルダをご確認のうえ、
              一覧の「招待を再送」から送り直してください。
            </AlertDescription>
          </Alert>
        ) : null}

        <DataTable
          columns={columns}
          rows={list.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={list.isPending}
          error={list.isError ? 'メンバーを取得できませんでした。' : undefined}
          emptyTitle="メンバーがいません"
        />

        <div className="rounded-lg border border-border bg-panel p-4">
          <p className="mb-2 text-sm font-medium">権限の説明</p>
          <dl className="space-y-2 text-sm">
            {(Object.keys(ROLE_LABEL) as Array<Member['role']>).map((r) => (
              <div key={r}>
                <dt className="font-medium text-foreground">{ROLE_LABEL[r]}</dt>
                <dd className="text-muted-foreground">{ROLE_HELP[r]}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </>
  );
}

function InviteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [email, setEmail] = React.useState('');
  const [displayName, setDisplayName] = React.useState('');
  const [role, setRole] = React.useState<'org_admin' | 'org_member'>('org_member');
  const queryClient = useQueryClient();

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  // 表示名は API で必須。未入力ならアドレスの @ より前を使う。
  const effectiveDisplayName = displayName.trim() || email.trim().split('@')[0] || '';

  const invite = useMutation({
    // 招待の実体は「メンバーの追加」。専用の `/members/invite` は存在しない。
    mutationFn: () =>
      api.post<{ id: string; orgUserId: string }>('/members', {
        email: email.trim(),
        displayName: effectiveDisplayName,
        roleCode: role,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.members() });
      setEmail('');
      setDisplayName('');
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={(n) => (invite.isPending ? undefined : onOpenChange(n))}>
      <DialogContent>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (emailOk) invite.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>メンバーを招待</DialogTitle>
            <DialogDescription>
              入力したメールアドレス宛に招待メールを送ります。受け取った方がパスワードを設定するとログインできます。
            </DialogDescription>
          </DialogHeader>

          <FormAlert error={invite.error} />

          <Field
            label="招待するメールアドレス"
            htmlFor="invite-email"
            required
            hint="このサービス以外のアドレス（会社の既存アドレスなど）でも構いません。"
            error={email.length > 0 && !emailOk ? 'メールアドレスの形式が正しくありません' : undefined}
          >
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </Field>

          <Field
            label="表示名"
            htmlFor="invite-name"
            hint="未入力の場合はメールアドレスの @ より前を使います。"
          >
            <Input
              id="invite-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="山田 太郎"
              autoComplete="off"
            />
          </Field>

          <Field label="権限" hint={ROLE_HELP[role]}>
            <Select value={role} onValueChange={(v) => setRole(v as 'org_admin' | 'org_member')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="org_admin">{ROLE_LABEL.org_admin}</SelectItem>
                <SelectItem value="org_member">{ROLE_LABEL.org_member}</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Alert variant="info">
            <AlertTitle>招待できるのは契約者（オーナー）のみです</AlertTitle>
            <AlertDescription>
              招待リンクの有効期限は 7 日間です。期限が切れた場合は再度招待してください。
            </AlertDescription>
          </Alert>

          <SubmitBar
            submitLabel="招待メールを送る"
            pending={invite.isPending}
            disabled={!emailOk}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}
